import api from '@/api';
import tableHeaders from '@/utils/tableHeaders';
import { PHENOL_IDS } from '@/utils/pheonl';
import { applyV2ColumnGroupVisibility as applyColumnGroupVisibility } from '@/utils/journalColumnGroups';
import { testItemDisplayName } from '@/utils/testItemDisplayName';

/** 작업표준물질 optional 사용량 기본 단위 */
export const DEFAULT_IN_USE_VOLUME_UNIT = 'mL';

const resolveInUseVolumeUnit = (...sources) => {
    for (const v of sources) {
        if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return DEFAULT_IN_USE_VOLUME_UNIT;
};

const setType = key => {
    if (key && key.indexOf('Date') >= 0) {
        return 'date';
    }
    return 'text';
};
/**
 * 헤더 변환 (headerKey -> key, text -> label)
 *
 * @param {object} header
 * @param {object} [opts]
 * @param {boolean} [opts.forceSampleNoLabel] sampleNo 열 라벨을 '시료번호'로 강제할지.
 *   폐기물은 엑셀 임포트 원본 헤더가 '시료명'인 경우가 있어 강제가 필요하다.
 *   토양·수질은 form_data 의 text 를 그대로 쓰는 것이 이관 전(release) 동작이므로 기본 false.
 */
const transformHeader = (header, opts = {}) => {
    header.type = setType(header.headerKey);
    header.key = header.headerKey;
    header.label = header.text;
    //시료번호인 경우 읽기 전용 처리 - 2026.05.29 김지윤
    if (header.key === 'sampleNo') {
        header.readOnly = true;
        if (opts.forceSampleNoLabel) header.label = '시료번호';
    }
    return header;
};

/** 폐기물 시험항목 여부 (라벨 강제 등 폐기물 전용 분기 판정) */
const isDisposalTestId = (testId) => String(testId || '').startsWith('DISPOSAL_');

/** 시험일지 불러오기 목록 개수 — 기본 최근 10건, 검색 시에도 최대 10건 */
export const BEFORE_JOURNAL_LIMIT = 10;

// headers 배열을 headersObj로 변환
const createHeadersObj = headers => {
    const headersObj = {};
    headers.forEach(el => {
        headersObj[el.key] = el;
    });
    return headersObj;
};

// optionalForm에 기본값 설정
const applyDefaultToOptionalForm = optionalForm => {
    if (optionalForm) {
        optionalForm.forEach(optional => {
            if (optional.default) {
                optional.value = optional.default;
            }
        });
    }
};

// subCategory 배열을 flat list로 변환
const flattenSubCategories = (dataList, getSubList, getSampleNo) => {
    const list = [];
    dataList.forEach((item, index) => {
        const subList = getSubList(item);
        const startIndex = list.length;
        const endIndex = startIndex + subList.length - 1;
        subList.forEach((sub, idx) => {
            const sampleNo = getSampleNo(sub, idx, index);
            list.push({
                rowId: index,
                testCategory: item.testCategory,
                ...sub,
                sampleNo,
                categoryIndex: {
                    start: startIndex,
                    end: endIndex,
                    length: endIndex - startIndex + 1,
                },
            });
        });
    });
    return { list, orgLength: dataList.length };
};

// subCategory 기반 flat 변환 공통 호출 (item.subCategory / 그룹 첫 행에 sampleNo)
const flattenBySubCategory = dataList =>
    flattenSubCategories(
        dataList,
        item => item.subCategory,
        (sub, idx) => (idx === 0 ? sub.sampleNo : ''),
    );

// analysisResult* 이면서 isCustom 이 아닌 formKey 목록
const getAnalysisResultKeys = tableForm =>
    Object.keys(tableForm).filter(
        key => key.includes('analysisResult') && !tableForm[key].isCustom,
    );

/** v2 자식 시험항목 표시명 */
const getV2ChildLabel = child =>
    child.name || child.lab_name || child.original_name || child.testId || '';

/**
 * VOC(HS)·CI,S 등: 단일 analysisResult 표 + isDataLinkTarget + 하위 testId → TCE/PCE·염소/황산 perChild 다행 UI.
 * 삼성분 moistureTable/ashTable/combustibleTable 은 isDataLinkTarget 이지만 블록당 1행(적층 block) —
 * perChild 다행 UI 아님. 요약(analysisResult, isSubCategory)만 다행.
 * 할로겐화 유기물질(DISPOSAL_TEST_085) 이중표:
 *   - analysisResult2(상세): perChildBlocks / testCategory 하위항목
 *   - analysisResult(요약): 부모명(할로겐화 유기물질) 1행 — perChild 아님
 */
const isLegacyChildLinkBlock = (formSection, children, formSections = []) => {
    if (!formSection || !Array.isArray(children) || children.length === 0) return false;
    if (formSection.isSubCategory === true) return true;
    // 요약표 등 isSubCategory:false 명시 시 perChild 제외 (유기인형 단일행)
    if (formSection.isSubCategory === false) return false;
    const key = formSection.key || '';
    const isAnalysisResultKey =
        key === 'analysisResult' || /^analysisResult\d+$/.test(key);
    // 이중표 요약(analysisResult): 상세(analysisResult2)가 있으면 부모명만 노출
    const hasDetail2 = (formSections || []).some(s => s?.key === 'analysisResult2');
    if (hasDetail2 && key === 'analysisResult' && formSection.isDataLinkTarget === true) {
        return false;
    }
    if (formSection.isDataLinkTarget === true && isAnalysisResultKey) return true;
    return false;
};

/** v2 perChild 행에 자식별 자리수·정량한계·기준 메타 주입 (레거시 makeSampleData 대응) */
const applyV2ChildMeta = (row, child) => {
    if (!child) return;
    if (child.digits != null) row.digits = child.digits;
    if (child.digits_last != null) row.digits_last = child.digits_last;
    if (child.concentration_limit != null) row.concentration_limit = child.concentration_limit;
    if (child.concentration_equal != null) {
        row.concentration_equal = !!child.concentration_equal;
    }
    if (child.criteria_area_1 != null) row.criteria_area_1 = child.criteria_area_1;
};

/** v2 시료별 active 자식 목록 (API childIsTest 또는 시료등록 testResult 기준) */
const getV2ActiveChildren = (sampleRow, children, testId) => {
    if (!children.length) return [];
    const map = sampleRow.childIsTest || {};
    const active = children.filter(c => map[c.testId] === true);
    if (active.length > 0) return active;

    const parentActive = map[testId] === true;
    const hasChildEntries = children.some(c => map[c.testId] !== undefined);
    if (parentActive && !hasChildEntries) return children;
    if (!sampleRow.sampleId) return children;
    return [];
};

/** v2 전체 시료 childIsTest 합집합(선택된 하위항목, children 순서 유지) */
const getV2SelectedChildrenUnion = (samples, children, testId) => {
    if (!children.length) return [];

    const seen = new Set();
    const union = [];
    (samples || []).forEach(sample => {
        getV2ActiveChildren(sample, children, testId).forEach(child => {
            const id = child.testId || child.id;
            if (id && !seen.has(id)) {
                seen.add(id);
                union.push(child);
            }
        });
    });

    if (union.length > 0) {
        const unionIds = new Set(union.map(c => c.testId || c.id));
        return children.filter(c => unionIds.has(c.testId || c.id));
    }

    const hasAnySelection = (samples || []).some(s => {
        const map = s.childIsTest || {};
        return Object.keys(map).length > 0;
    });
    return hasAnySelection ? [] : children;
};

/**
 * 선택 하위항목명과 섹션 name 이 일치하는 표만 노출 (발열량·강열감량형 다표).
 * 하위항목이 없거나 섹션명이 어떤 자식과도 안 맞으면 기존처럼 전부 표시.
 */
const filterFormKeysBySelectedChildren = (formKeys, formData, v2Samples, testId) => {
    const children = v2Samples?.children || [];
    if (!children.length || !Array.isArray(formKeys) || !formKeys.length) return formKeys;

    const selected = getV2SelectedChildrenUnion(
        v2Samples?.samples || [],
        children,
        testId || v2Samples?.testId,
    );
    const hasExplicitSelection = (v2Samples?.samples || []).some(sample =>
        children.some(child =>
            Object.prototype.hasOwnProperty.call(
                sample?.childIsTest || {},
                child.testId || child.id,
            ),
        ),
    );
    // 선택 정보가 전혀 없으면(레거시) 전부 표시
    if (!selected.length && !hasExplicitSelection) return formKeys;

    const childLabels = new Set(
        children.map(c => String(c.lab_name || c.name || '').trim()).filter(Boolean),
    );
    const selectedIds = new Set(
        selected.map(c => c.testId || c.id).filter(Boolean),
    );
    const selectedLabels = new Set(
        selected.map(c => String(c.lab_name || c.name || '').trim()).filter(Boolean),
    );
    const sections = [...(formData?.[0] || []), ...(formData?.[1] || [])];
    const byKey = new Map(sections.filter(s => s?.key).map(s => [s.key, s]));

    return formKeys.filter(key => {
        const section = byKey.get(key);
        if (!section) return true;
        if (section.testId) return selectedIds.has(section.testId);
        const name = String(section.name || '').trim();
        // 자식 항목명과 동일한 섹션만 선택 필터 대상 (시약 등 공통 섹션은 유지)
        if (!name || !childLabels.has(name)) return true;
        return selectedLabels.has(name);
    });
};

/** v2 CCV 행 생성 — 선택 하위항목당 1행 (레거시 createSubCategoryRows 대응) */
const buildV2CcvRows = ({ section, selectedChildren }) => {
    const headers = section.headers || [];
    const headerKeys = headers.map(h => h.key);
    return (selectedChildren || []).map(child => {
        const row = {};
        headerKeys.forEach(k => { row[k] = ''; });
        headers.forEach(h => {
            if (h.default !== undefined && h.default !== null && h.key) {
                row[h.key] = h.default;
            }
        });
        if (headerKeys.includes('testCategory')) {
            row.testCategory = getV2ChildLabel(child);
        } else if (headerKeys.includes('name')) {
            row.name = getV2ChildLabel(child);
        }
        applyV2ChildMeta(row, child);
        return row;
    });
};

const v2CcvRowKey = row => String(row.testCategory || '').trim();

/** v2 CCV 재시딩 시 testCategory 기준 기존 입력값 머지 */
const mergeV2CcvRows = (existingList, newRows) => {
    const map = new Map();
    (existingList || []).forEach(row => {
        const k = v2CcvRowKey(row);
        if (k) map.set(k, row);
    });
    return newRows.map(row => {
        const prev = map.get(v2CcvRowKey(row));
        if (!prev) return row;
        const merged = { ...row, ...prev, testCategory: row.testCategory };
        return merged;
    });
};

/** v2 perChild 블록 평탄 행 생성 (수질 중금속 flattenBySubCategory 패턴) */
const buildV2PerChildRows = ({
    section,
    samples,
    children,
    testId,
}) => {
    const headerKeys = (section.headers || []).map(h => h.key);
    const list = [];

    samples.forEach((sample, sampleIndex) => {
        const activeChildren = getV2ActiveChildren(sample, children, testId);
        const targets = activeChildren.length > 0 ? activeChildren : children;
        const startIndex = list.length;
        const endIndex = startIndex + targets.length - 1;

        targets.forEach((child, idx) => {
            const row = {};
            headerKeys.forEach(k => { row[k] = ''; });
            row.sampleId = sample.sampleId;
            row.sampleName = sample.sampleName;
            row.sampleNo = idx === 0 ? sample.sampleNo : '';
            if (headerKeys.includes('testCategory')) {
                row.testCategory = getV2ChildLabel(child);
            }
            // 레거시 makeSampleData 와 동일: 자식별 자리수·정량한계·기준을 행 메타로 주입
            applyV2ChildMeta(row, child);
            list.push({
                ...row,
                rowId: sampleIndex,
                categoryIndex: {
                    start: startIndex,
                    end: endIndex,
                    length: endIndex - startIndex + 1,
                },
            });
        });
    });

    return list;
};

/** v2 단일행 블록 (요약·ccv 등) */
const buildV2SingleRows = ({ section, samples, parentLabel, fillTestCategory }) => {
    const headerKeys = (section.headers || []).map(h => h.key);
    return samples.map(sample => {
        const row = {};
        headerKeys.forEach(k => { row[k] = ''; });
        row.sampleId = sample.sampleId;
        row.sampleNo = sample.sampleNo;
        row.sampleName = sample.sampleName;
        if (fillTestCategory && headerKeys.includes('testCategory')) {
            row.testCategory = parentLabel;
        }
        return row;
    });
};

/** v2 perChild 재시딩 시 기존 입력값 머지 (구조는 newRows 기준) */
const mergeV2RowValues = (base, prev, skipKeys = []) => {
    const merged = { ...base };
    Object.keys(prev).forEach(k => {
        if (skipKeys.includes(k)) return;
        if (prev[k] !== '' && prev[k] != null) {
            merged[k] = prev[k];
        }
    });
    return merged;
};

/** 저장/깨진 리스트를 시료별 그룹(rowId·categoryIndex·sampleNo 병합)으로 정규화 */
const normalizeV2PerChildGroupedList = list => {
    if (!Array.isArray(list) || list.length === 0) return list;

    const groups = [];
    let currentKey = null;
    let currentGroup = null;

    const groupKey = row => {
        if (row.categoryIndex != null && row.categoryIndex.start != null) {
            return `cat:${row.categoryIndex.start}`;
        }
        if (row.rowId != null) return `rid:${row.rowId}`;
        if (row.sampleId != null && String(row.sampleId).trim() !== '') {
            return `id:${row.sampleId}`;
        }
        return `no:${row.sampleName || row.sampleNo || ''}`;
    };

    list.forEach(row => {
        const key = groupKey(row);
        if (key !== currentKey || !currentGroup) {
            currentKey = key;
            currentGroup = { sampleNo: row.sampleNo, rows: [] };
            groups.push(currentGroup);
        }
        currentGroup.rows.push(row);
        if (!currentGroup.sampleNo && row.sampleNo) {
            currentGroup.sampleNo = row.sampleNo;
        }
    });

    const normalized = [];
    groups.forEach((group, sampleIndex) => {
        const startIndex = normalized.length;
        const endIndex = startIndex + group.rows.length - 1;
        const groupSampleNo =
            group.sampleNo ||
            group.rows.find(r => r.sampleNo)?.sampleNo ||
            '';
        group.rows.forEach((row, idx) => {
            normalized.push({
                ...row,
                sampleNo: idx === 0 ? groupSampleNo : '',
                rowId: sampleIndex,
                categoryIndex: {
                    start: startIndex,
                    end: endIndex,
                    length: endIndex - startIndex + 1,
                },
            });
        });
    });
    return normalized;
};

/** 시험항목 라벨 매칭 — "Trichloroethylene (TCE)" ↔ "TCE" */
const testCategoryMatchKeys = label => {
    const s = String(label || '').trim();
    if (!s) return [];
    const keys = new Set([s]);
    const paren = s.match(/\(([^)]+)\)\s*$/);
    if (paren) keys.add(paren[1].trim());
    return [...keys];
};

/** perChild 그룹(TCE+PCE 등)에서 시료번호·sampleId 보강 — 보조행(sampleNo 빈값)용 */
const resolvePerChildGroupSampleKeys = rows => {
    const first =
        rows.find(r => r.sampleNo != null && String(r.sampleNo).trim() !== '') ||
        rows[0] ||
        {};
    return {
        sampleNo: first.sampleNo != null ? String(first.sampleNo).trim() : '',
        sampleId: first.sampleId != null ? String(first.sampleId).trim() : '',
    };
};

const buildPerChildRowIdIndex = rows => {
    const byRowId = new Map();
    (rows || []).forEach(row => {
        if (row.rowId == null) return;
        if (!byRowId.has(row.rowId)) byRowId.set(row.rowId, []);
        byRowId.get(row.rowId).push(row);
    });
    const index = new Map();
    byRowId.forEach((arr, rid) => {
        arr.sort(
            (a, b) =>
                (a.categoryIndex?.start ?? 0) - (b.categoryIndex?.start ?? 0),
        );
        arr.forEach((row, i) => index.set(`${rid}\0${i}`, row));
    });
    return { byRowId, index };
};

const mergeV2PerChildRows = (existingList, newRows) => {
    const existing = normalizeV2PerChildGroupedList(existingList || []);
    const bySampleId = new Map();
    const bySampleNo = new Map();
    const { byRowId: existingByRowId, index: byRowIdIndex } =
        buildPerChildRowIdIndex(existing);
    const { byRowId: newByRowId } = buildPerChildRowIdIndex(newRows);

    existingByRowId.forEach(groupRows => {
        const { sampleNo, sampleId } = resolvePerChildGroupSampleKeys(groupRows);
        groupRows.forEach(row => {
            testCategoryMatchKeys(row.testCategory).forEach(tc => {
                if (sampleId) bySampleId.set(`${sampleId}\0${tc}`, row);
                if (sampleNo) bySampleNo.set(`${sampleNo}\0${tc}`, row);
            });
        });
    });

    const resolveKeys = row => {
        let sampleNo = row.sampleNo != null ? String(row.sampleNo).trim() : '';
        let sampleId = row.sampleId != null ? String(row.sampleId).trim() : '';
        if ((!sampleNo || !sampleId) && row.rowId != null && newByRowId.has(row.rowId)) {
            const g = resolvePerChildGroupSampleKeys(newByRowId.get(row.rowId));
            if (!sampleNo) sampleNo = g.sampleNo;
            if (!sampleId) sampleId = g.sampleId;
        }
        return { sampleNo, sampleId };
    };

    const findPrev = row => {
        const { sampleNo, sampleId } = resolveKeys(row);
        for (const tc of testCategoryMatchKeys(row.testCategory)) {
            if (sampleId && bySampleId.has(`${sampleId}\0${tc}`)) {
                return bySampleId.get(`${sampleId}\0${tc}`);
            }
            if (sampleNo && bySampleNo.has(`${sampleNo}\0${tc}`)) {
                return bySampleNo.get(`${sampleNo}\0${tc}`);
            }
        }
        if (row.rowId != null && newByRowId.has(row.rowId)) {
            const group = newByRowId.get(row.rowId);
            const idx = group.indexOf(row);
            if (idx >= 0) {
                const hit = byRowIdIndex.get(`${row.rowId}\0${idx}`);
                if (hit) return hit;
            }
        }
        return null;
    };

    return newRows.map(row => {
        const prev = findPrev(row);
        if (!prev) return row;
        // testCategory·자식 메타는 신규 시딩 유지
        // (구버전 저장 '시료분석결과' 가 위치 매칭으로 염소이온 행을 덮어쓰는 것 방지)
        return mergeV2RowValues(row, prev, [
            'rowId',
            'categoryIndex',
            'sampleNo',
            'testCategory',
            'digits',
            'concentration_limit',
            'concentration_equal',
            'criteria_area_1',
        ]);
    });
};

/** v2 단일행 블록 재시딩 시 기존 입력값 머지 */
const mergeV2SingleRows = (existingList, newRows) => {
    const bySampleId = new Map();
    const bySampleNo = new Map();
    (existingList || []).forEach(row => {
        const sid = row.sampleId != null ? String(row.sampleId).trim() : '';
        const sno = row.sampleNo != null ? String(row.sampleNo).trim() : '';
        if (sid) bySampleId.set(sid, row);
        if (sno) bySampleNo.set(sno, row);
    });
    const findPrev = row => {
        const sid = row.sampleId != null ? String(row.sampleId).trim() : '';
        const sno = row.sampleNo != null ? String(row.sampleNo).trim() : '';
        if (sid && bySampleId.has(sid)) return bySampleId.get(sid);
        if (sno && bySampleNo.has(sno)) return bySampleNo.get(sno);
        return null;
    };
    return newRows.map(row => {
        const prev = findPrev(row);
        if (!prev) return row;
        return mergeV2RowValues(row, prev, ['sampleNo', 'sampleId', 'sampleName']);
    });
};

/** 저장 직전: perChild analysisResult 에 부모명/섹션명만 들어간 행 경고 (엑셀·성적서 매칭 실패 예방) */
const warnPerChildAnalysisBeforeSave = (journalTableForm, journalForm, v2Samples) => {
    const children = v2Samples?.children || [];
    if (!children.length || !journalTableForm) return;
    // 경고 탐지용이라 표시 규칙과 무관하게 두 이름을 모두 본다
    // (규칙이 name 으로 바뀌어도 lab_name 으로 저장된 옛 행을 놓치지 않는다)
    const forbidden = new Set(
        ['시료분석결과', journalForm?.name, journalForm?.lab_name]
            .filter((s) => s && String(s).trim())
            .map((s) => String(s).trim()),
    );
    const childLabels = new Set(children.map(getV2ChildLabel));

    Object.keys(journalTableForm).forEach((key) => {
        if (!key.includes('analysisResult')) return;
        const section = journalTableForm[key];
        if (!section?.isSubCategory || !Array.isArray(section.list)) return;
        section.list.forEach((row) => {
            const tc = String(row?.testCategory || '').trim();
            if (!tc || childLabels.has(tc)) return;
            if (forbidden.has(tc)) {
                console.warn(
                    '[journal] perChild analysisResult: 시험항목이 부모/섹션명으로 저장됨 — 수동 동기화 후 재저장 권장',
                    { formKey: key, testCategory: tc, sampleNo: row.sampleNo },
                );
            }
        });
    });
};

const applyV2PerChildSectionMeta = (journalTableForm, v2Samples, journalForm = null, formData = null) => {
    const children = v2Samples?.children || [];
    if (!children.length) return;

    const samples = v2Samples?.samples || [];
    const testId = v2Samples?.testId;
    const selectedUnion = getV2SelectedChildrenUnion(samples, children, testId);
    const targets = selectedUnion.length > 0 ? selectedUnion : children;
    const childLabels = targets.map(getV2ChildLabel);
    const ccvSubCategory = targets.map(c => ({ testCategory: getV2ChildLabel(c) }));

    const applyPerChildMeta = key => {
        const section = journalTableForm[key];
        if (!section) return;
        section.isSubCategory = true;
        section.orgLength = samples.length ?? section.orgLength;
        section.subCategoryLength = targets.length;
        if (childLabels.length > 0) {
            section.uniqueSubCategory = childLabels;
        }
        if (Array.isArray(section.list) && section.list.length > 0) {
            section.list = normalizeV2PerChildGroupedList(section.list);
        }
    };

    const perChildBlocks = v2Samples?.perChildBlocks;
    if (Array.isArray(perChildBlocks) && perChildBlocks.length > 0) {
        perChildBlocks.forEach(applyPerChildMeta);
    }

    // VOC(HS) 등: isDataLinkTarget + 하위항목 — 수질/토양과 동일 subCategory UI
    const formSections = formData
        ? [...(formData[0] || []), ...(formData[1] || [])]
        : [];
    formSections.forEach(s => {
        if (!s?.key || !isLegacyChildLinkBlock(s, children, formSections)) return;
        applyPerChildMeta(s.key);
    });

    const ccvSection = journalTableForm.ccv;
    const hasCcvTestCategory = (ccvSection?.headers || []).some(h => h.key === 'testCategory');
    if (ccvSection && hasCcvTestCategory && targets.length > 0) {
        ccvSection.uniqueSubCategory = ccvSubCategory;
        ccvSection.subCategoryLength = targets.length;
        if (journalForm) {
            journalForm.isCcvSubCategory = true;
            journalForm.uniqueSubCategory = ccvSubCategory;
        }
    }
};

/** v2 columnGroups — 선택 하위항목(C/H/N/S/O/CI)에 맞춰 elementTable 컬럼 isHide 갱신 */
const applyV2ColumnGroupVisibility = (journalTableForm, formData, v2Samples) => {
    if (!v2Samples?.children?.length) return;
    applyColumnGroupVisibility(
        journalTableForm,
        formData,
        v2Samples,
        getV2SelectedChildrenUnion,
        getV2ChildLabel,
    );
};

/** v2 블록 시딩 공통 */
const seedV2JournalBlocks = ({
    result,
    formData,
    v2Samples,
    parentLabel,
    mergeExisting = false,
}) => {
    const baseSamples = v2Samples.samples;
    const blockKeys = Array.isArray(v2Samples.blocks)
        ? v2Samples.blocks
        : [];
    const perChildSet = new Set(v2Samples.perChildBlocks || []);
    const children = v2Samples.children || [];
    const testId = v2Samples.testId;
    const formSections = [...(formData[0] || []), ...(formData[1] || [])];
    const selectedUnion = getV2SelectedChildrenUnion(baseSamples, children, testId);
    const selectionTargets = selectedUnion.length > 0 ? selectedUnion : children;
    const activeBlockKeys = new Set(
        filterFormKeysBySelectedChildren(
            blockKeys,
            formData,
            v2Samples,
            testId,
        ),
    );

    blockKeys.forEach(key => {
        const section = result[key];
        if (!section) return;

        const formSection = formSections.find(s => s.key === key);
        if (!activeBlockKeys.has(key)) {
            section.list = [];
            section.isSubCategory = false;
            section.orgLength = 0;
            section.subCategoryLength = 0;
            delete section.uniqueSubCategory;
            return;
        }
        const sectionTestId = formSection?.testId;
        const sectionSamples = sectionTestId
            ? baseSamples.filter(sample =>
                getV2ActiveChildren(sample, children, testId)
                    .some(child => (child.testId || child.id) === sectionTestId),
            )
            : baseSamples;
        const isDataLinkTarget = formSection?.isDataLinkTarget === true;
        const isLegacyChildLink = isLegacyChildLinkBlock(formSection, children, formSections);
        const isPerChild = perChildSet.has(key) && children.length > 0;
        const usePerChildRows = isPerChild || isLegacyChildLink;
        const hasCcvTestCategory = (section.headers || []).some(
            h => h.key === 'testCategory' || h.key === 'name',
        );
        const isCcvSection = key === 'ccv' && hasCcvTestCategory;
        const isCcvWithChildren = isCcvSection && children.length > 0;
        // 단일 항목(자식 없음): 토양/수질과 같이 CCV 시험항목명을 부모 시험명으로 고정
        const isCcvSingle = isCcvSection && children.length === 0;
        const resolvedParentLabel =
            parentLabel || formSection?.name || '';
        // 이중표 요약(analysisResult): 시험항목명은 섹션명이 아니라 시험명(할로겐화 유기물질)
        const singleLabel =
            key === 'analysisResult' && isDataLinkTarget
                ? (parentLabel || formSection?.name || '')
                : (formSection?.name || parentLabel);

        let newRows;
        if (isCcvWithChildren) {
            newRows = buildV2CcvRows({
                section,
                selectedChildren: selectionTargets,
            });
        } else if (isCcvSingle) {
            // CCV는 시료 단위가 아님 — 시험항목 1행(또는 기존 행 유지) + 시험명 고정
            if (mergeExisting && Array.isArray(section.list) && section.list.length > 0) {
                section.list = section.list.map(row => {
                    const tc = String(row.testCategory || row.name || '').trim();
                    if (tc) return row;
                    const next = { ...row };
                    if ((section.headers || []).some(h => h.key === 'testCategory')) {
                        next.testCategory = resolvedParentLabel;
                    } else if ((section.headers || []).some(h => h.key === 'name')) {
                        next.name = resolvedParentLabel;
                    }
                    return next;
                });
                newRows = null; // 아래에서 list 재할당 스킵
            } else {
                newRows = buildV2CcvRows({
                    section,
                    selectedChildren: [
                        {
                            name: resolvedParentLabel,
                            lab_name: resolvedParentLabel,
                        },
                    ],
                });
            }
        } else if (usePerChildRows) {
            newRows = buildV2PerChildRows({
                section,
                samples: sectionSamples,
                children,
                testId,
            });
        } else {
            newRows = buildV2SingleRows({
                section,
                samples: sectionSamples,
                parentLabel: singleLabel,
                fillTestCategory: isDataLinkTarget,
            });
        }

        if (newRows) {
            if (mergeExisting) {
                if (isCcvWithChildren) {
                    section.list = mergeV2CcvRows(section.list, newRows);
                } else if (usePerChildRows) {
                    section.list = mergeV2PerChildRows(section.list, newRows);
                } else {
                    section.list = mergeV2SingleRows(section.list, newRows);
                    // 이중표 요약(analysisResult): 시험항목은 부모명 고정
                    // (구버전 perChild 저장값이 머지로 하위항목명을 남기지 않도록)
                    if (
                        key === 'analysisResult' &&
                        isDataLinkTarget &&
                        singleLabel
                    ) {
                        section.list = section.list.map(row => ({
                            ...row,
                            testCategory: singleLabel,
                        }));
                    }
                }
            } else {
                section.list = newRows;
            }
        }

        if (isCcvWithChildren) {
            section.uniqueSubCategory = selectionTargets.map(c => ({
                testCategory: getV2ChildLabel(c),
            }));
            section.subCategoryLength = selectionTargets.length;
        } else if (isCcvSingle) {
            section.isSubCategory = false;
            section.subCategoryLength = 1;
            delete section.uniqueSubCategory;
        } else if (usePerChildRows) {
            section.isSubCategory = true;
            section.orgLength = sectionSamples.length;
            section.subCategoryLength = selectionTargets.length;
            section.uniqueSubCategory = selectionTargets.map(getV2ChildLabel);
        } else {
            section.isSubCategory = false;
            section.orgLength = 0;
            section.subCategoryLength = 1;
            delete section.uniqueSubCategory;
        }
    });
};

// form 배열(headerKey 등)로부터 headers/headersObj 를 대상 객체에 세팅
const assignHeaders = (target, formArray, opts = {}) => {
    // map(transformHeader) 로 넘기면 두 번째 인자에 인덱스가 들어가므로 명시 호출한다
    const headers = formArray.map((h) => transformHeader(h, opts));
    target.headers = headers;
    target.headersObj = createHeadersObj(headers);
};

// 문자열 trim 후 비교 — null 을 빈 문자열로 취급(느슨). applyCustomCategoryImport 전용
const trimEqAsEmpty = (a, b) => {
    const sa = a == null ? '' : String(a).trim();
    const sb = b == null ? '' : String(b).trim();
    return sa === sb;
};

// 문자열 trim 후 비교 — null 이면 무조건 false(엄격). setImportSampleData 전용
const trimEqNonNull = (a, b) => {
    if (a == null || b == null) return false;
    return String(a).trim() === String(b).trim();
};

// form_data 항목의 isDeviation, isSubCategory를 journalTableForm에 매핑
const applyFormDataOverrides = (state, formData, uniqueSubCategory) => {
    const allItems = [...(formData[0] || []), ...(formData[1] || [])];
    allItems.forEach(item => {
        if (!state.journalTableForm[item.key]) return;
        if (item.isDeviation !== undefined) {
            state.journalTableForm[item.key].exceptDeviation = item.isDeviation;
        }
        if (item.isSubCategory !== undefined) {
            state.journalTableForm[item.key].isSubCategory = item.isSubCategory;
            // v2 perChild 등: 빈 uniqueSubCategory 로 시딩된 목록을 덮어쓰지 않음
            if (Array.isArray(uniqueSubCategory) && uniqueSubCategory.length > 0) {
                state.journalTableForm[item.key].uniqueSubCategory = uniqueSubCategory;
            }
        }
        if (item.isDataLinkTarget !== undefined) {
            state.journalTableForm[item.key].isDataLinkTarget = item.isDataLinkTarget;
        }
    });
};

// CCV 관련 설정을 journalForm에 적용
const applyCcvSettings = (state, formData, v2Samples = null) => {
    const ccvItem = formData[1]?.find(el => el.key === 'ccv');
    if (ccvItem) {
        state.journalForm.exceptDeviation = ccvItem.isDeviation ?? null;
        state.journalForm.isCcvSubCategory = ccvItem.isSubCategory;
    }

    const v2meta = formData?.[2];
    if (
        v2meta?.engine === 'v2' &&
        v2Samples?.children?.length &&
        state.journalTableForm?.ccv
    ) {
        const hasCcvTestCategory = (state.journalTableForm.ccv.headers || []).some(
            h => h.key === 'testCategory',
        );
        const selected = getV2SelectedChildrenUnion(
            v2Samples.samples,
            v2Samples.children,
            v2Samples.testId || state.testId,
        );
        if (hasCcvTestCategory && selected.length > 0) {
            state.journalForm.isCcvSubCategory = true;
        }
    }

    return ccvItem;
};

// 시험방법 옵션 설정 — method 없으면 옵션 비워 섹션 비노출(물리적조성 등)
const applyTestMethodSettings = (state, data) => {
    const methods = Array.isArray(data.method) ? data.method.filter(Boolean) : [];
    state.journalForm.testMethod = methods[0] || '';
    state.journalForm.testMethodOptions = methods.map(item => ({
        text: item,
        value: item,
    }));
};

// testId별 customCategory 템플릿 설정
const CUSTOM_CATEGORY_CONFIG = {
    WATER_TEST_029: {
        formKey: 'analysisResult',
        testCategories: ['총노말헥산추출물질', 'n-Hexane(광유류)', 'n-Hexane(동식물성유지류)'],
        template: {
            beforeReagentConcentration: '',
            afterReagentConcentration: '',
            beforeBackgroundConcentration: '',
            afterBackgroundConcentration: '',
            sampleCount: '',
            lastConcentration: '',
            markedConcentration: ''
        }
    },
    WATER_TEST_080: {
		formKey: 'analysisResult4_sample',
        testCategories: ['Control', '6.25', '12.5', '25', '50', '100'],
        template: {
            inhibitionRateControl1: 0,
            inhibitionRateControl2: 0,
            inhibitionRateControl3: 0,
            inhibitionRateControl4: 0,
            inhibitionRateControl5: 0,
            sampleId: '',
            sampleName: '',
        },
    },
    WATER_TEST_119: {
		formKey: 'analysisResult2_sample',
        testCategories: ['P-알칼리도', 'M-알칼리도'],
        template: {
            reagentConcentration: '',
            backgroundConcentration: 1.0101,
            dilutionFactor: '',
            lastConcentration: '',
            markedConcentration: '',
            digits: 1,
        },
    },
    WATER_TEST_179: {
		formKey: 'analysisResult',
        testCategories: ['HCO3-'],
        template: {
            confirmAlkalinityRelationship: '',
            lastConcentration: '',
            markedConcentration: '',
        },
    }, 
    WATER_TEST_017: [
        {
            formKey: 'plantingLiquid', 
            testCategories: ['BOD', 'BOD', 'BOD', 'BOD'],
            ////시료수만큼곱한게나와서 4로 고정하려고 추가
            fixedRows:true,
            template: {
                sampleCount: '', total: '', beforeDO: '', afterDO: '',
                moi: '', dilutionFactor: 1, oxygenConsumption: '',
                bodConsumption: '', scf: '',
            },
        },
        {
            formKey: 'analysisResult',  
            testCategories: ['BOD', 'BOD', 'BOD'],
            template: {
                sampleCount: '', total: '', beforeDO: '', afterDO: '',
                moi: '', dilutionFactor: 1, oxygenConsumption: '',
                lastConcentration2: '', lastConcentration: '', markedConcentration2: '',
            },
        },
    ],
    WATER_TEST_777: {
		formKey: 'analysisResult',
        testCategories: ['BOD', 'BOD', 'BOD'],
        template: {
            sampleCount: '',
            total: '',
            beforeDO: '',
            afterDO: '',
            moi: '',
            dilutionFactor: 1,
            oxygenConsumption: '',
            lastConcentration2: '',
            lastConcentration: '',
            markedConcentration2: '',
        },
    },
};
function applyCustomCategoryImport(state, testId, activeSamples) {
    let configs = CUSTOM_CATEGORY_CONFIG[testId];
    if (!configs) return;
    if (!Array.isArray(configs)) configs = [configs];

    configs.forEach(config => {
        if (!state.journalTableForm[config.formKey]?.isCustom) return;

        const customCategory = config.testCategories.map(tc => ({
            sampleNo: '',
            testCategory: tc,
            ...config.template,
        }));

        if (config.fixedRows) {
        // 시료수 무관, 고정 행만 세팅
        // 수동 동기화 시 사용자가 입력한 기존 값(식종액 등)이 빈 템플릿으로
        // 초기화되지 않도록, 행 인덱스 기준으로 기존 값을 병합한다.
        const existing = state.journalTableForm[config.formKey].list || [];
        const merged = customCategory.map((row, idx) => {
            const prev = existing[idx];
            if (!prev) return { ...row };
            return { ...row, ...prev, testCategory: row.testCategory };
        });
        state.journalTableForm[config.formKey].orgLength = 0; 
        state.journalTableForm[config.formKey].isSubCategory = false;
        state.journalTableForm[config.formKey].subCategoryLength = customCategory.length;
        state.journalTableForm[config.formKey].uniqueSubCategory = customCategory;
        state.journalTableForm[config.formKey].list = [...merged];
        return;
        }

        const subCategoryData = activeSamples;
        const existing = state.journalTableForm[config.formKey].list || [];

        const { list, orgLength } = flattenSubCategories(
            subCategoryData,
            () => customCategory,
            (sub, idx, index) => idx === 0 ? subCategoryData[index]?.sampleNo : '',
        );
        //   사용자가 입력/저장한 값(0.0000 등) 이 customCategory.template 의 빈값으로
        //   초기화되는 회귀가 있어 trim 비교로 보강한다.
        const usedExistingIdx = new Set();
        const merged = list.map((item) => {
            const prevIdx = existing.findIndex((e, i) =>
                !usedExistingIdx.has(i) &&
                trimEqAsEmpty(e.sampleNo, item.sampleNo) &&
                trimEqAsEmpty(e.testCategory, item.testCategory)
            );
            if (prevIdx < 0) return item;
            usedExistingIdx.add(prevIdx);
            const prev = existing[prevIdx];
            return {
                ...item,
                ...prev,
                sampleNo: item.sampleNo,
                rowId: item.rowId,
                categoryIndex: item.categoryIndex,
            };
        });

        state.journalTableForm[config.formKey].orgLength = orgLength;
        state.journalTableForm[config.formKey].isSubCategory = true;
        state.journalTableForm[config.formKey].subCategoryLength = customCategory.length;
        state.journalTableForm[config.formKey].uniqueSubCategory = customCategory;
        state.journalTableForm[config.formKey].list = [...merged];
    });
}

/**
 * 수동 동기화(importSampleData) 시 시험항목 표시명.
 * 규칙 본체는 utils/testItemDisplayName 에 있다 — CCV 기본값도 같은 것을 쓴다.
 */
const importCategoryLabel = (cat, category) => testItemDisplayName(cat, category);

const state = {
    projectId: '',
    testId: '',
    machineList: [],
    beforeJournal: [],
    journalForm: {},
    journalTableForm: {},
    journalFormKeys: {
        0: [],
        1: [],
    },
    analysisResultList: [],
    newAnalysisList: [],
    // v2(동적 일지) 시료 base: { engine, blocks:[key], samples:[{sampleId,sampleNo,sampleName}] }
    v2Samples: null,
};

const getters = {
    beforeJournal: state => {
        return state.beforeJournal.map(item => ({
            ...item,
            text: `${item.projectCode}${item.cvvType ? ' : ' : ''}${item.cvvType ? item.cvvType : ''}${item.testingSubstanceDate ? ' : ' : ''}${item.testingSubstanceDate ? item.testingSubstanceDate : ''}`,
            value: item.testId,
        }));
    },
    journalTableForm: state => state.journalTableForm,
    journalForm: state => state.journalForm,
    journalFormKeys: state => state.journalFormKeys,
    v2Samples: state => state.v2Samples,
    /** 선택 하위항목에 맞춰 필터된 form 키 (발열량 고위/저위 등) */
    visibleJournalFormKeys: state => {
        const keys = [
            ...(state.journalFormKeys?.[0] || []),
            ...(state.journalFormKeys?.[1] || []),
        ];
        const formData = state.journalForm?.form_data;
        if (!formData || state.v2Samples?.engine !== 'v2') return { 0: state.journalFormKeys?.[0] || [], 1: state.journalFormKeys?.[1] || [] };
        const filtered = filterFormKeysBySelectedChildren(
            keys,
            formData,
            { ...state.v2Samples, testId: state.testId },
            state.testId,
        );
        const set0 = new Set(state.journalFormKeys?.[0] || []);
        return {
            0: filtered.filter(k => set0.has(k)),
            1: filtered.filter(k => !set0.has(k)),
        };
    },
    getTableFormData: state => formKey => {
        return state.journalTableForm[formKey];
    },
    analysisResultList: state => state.analysisResultList,
    machineList: state => {
        return state.machineList.map(item => ({
            ...item,
            text: item.name,
            value: item.data,
        }));
    },
};

const mutations = {
    setProjectIds: (state, data) => {
        state.projectId = data.projectId;
        state.testId = data.testId;
    },
    setMachine: (state, data) => {
        state.machineList = [...data];
    },
    setBeforeJournal: (state, data) => {
        state.beforeJournal = [...data];
    },
    setFormData: (state, data) => {
        const { key, value } = data;
        if (key === 'machine') {
            state.journalForm[key].data = value;
            state.journalTableForm[key].data = value;
            state.journalTableForm[key].list = value;
        }
    },
    _setJournalFormData: (state, data) => {
        state.journalForm = {};
        state.journalTableForm = {};

        // 방어: form_data 미등록(예: form_data NULL) 항목은 여기서 크래시하지 않고
        // 빈 배열로 정규화한다. (data.form_data[1].map 널 참조로 일지 진입이 통째로 깨지던 문제)
        if (!Array.isArray(data.form_data)) data.form_data = [];
        if (!Array.isArray(data.form_data[0])) data.form_data[0] = [];
        if (!Array.isArray(data.form_data[1])) data.form_data[1] = [];

        // 폐기물만 sampleNo 라벨을 '시료번호'로 강제한다 (엑셀 임포트 원본이 '시료명'인 경우 대응).
        // 토양·수질은 form_data 의 text 를 그대로 쓰는 이관 전 동작을 유지한다.
        const forceSampleNoLabel = isDisposalTestId(data.id ?? data.testId ?? data.test_id);

        //form_data key 순서 기억
        state.journalFormKeys[0] = data.form_data[0].map(el => el.key);
        state.journalFormKeys[1] = data.form_data[1].map(el => el.key);
        const form_data = [...data.form_data[0], ...data.form_data[1]];
        const result = {};

        form_data.forEach(form => {
            const headers = form.form.map((h) =>
                transformHeader(h, { forceSampleNoLabel: forceSampleNoLabel }),
            );
            const stdUseHdr = headers.find((h) => h.key === 'stdUseVolume');
            const stdUnitDefault =
                stdUseHdr?.unit != null && String(stdUseHdr.unit).trim() !== ''
                    ? String(stdUseHdr.unit).trim()
                    : DEFAULT_IN_USE_VOLUME_UNIT;
            const optionalForm = form.optionalForm
                ? form.optionalForm.map(el => {
                    const o = { ...el, value: '' };
                    if (o.key === 'inUseVolume') {
                        if (o.labelUnit === undefined || o.labelUnit === '') {
                            // 괄호 단위 기본 = 표준 사용량 단위 (값 옆 unit 과 분리)
                            o.labelUnit = stdUnitDefault;
                        }
                        if (o.unit === undefined || o.unit === '') {
                            o.unit = DEFAULT_IN_USE_VOLUME_UNIT;
                        }
                    }
                    return o;
                    })
                : [];

            result[form.key] = {
                headers: headers,
                headersObj: createHeadersObj(headers),
                list: [],
                key: form.key,
                name: form.name,
                isCustom: form.isCustom,
                optionalForm: optionalForm,
            };

            //2025.11.06 기본값이 있을 경우 optionalForm 에 기본값 추가하기
            applyDefaultToOptionalForm(result[form.key].optionalForm);
        });

        //사용기기 리스트 데이터 세팅하기 (devices: 발열량 등 복수 모델/제작회사)
        // name(기기 모델명)은 journalForm.machine 에만 둔다 — journalTableForm.machine.name 은
        // journalTableForm.sectionTitle 로 쓰여 조작조건 표 제목에 기기명이 노출된다.
        result['machine'] = {
            headers: tableHeaders.useDeviceTableHeader.map((h) => transformHeader(h)),
            list: data['machine']?.data ? [...data['machine'].data] : [],
            maker: data['machine']?.maker || '',
            devices: Array.isArray(data['machine']?.devices)
                ? data['machine'].devices.map((d) => ({ ...d }))
                : undefined,
        };
        //사용 시약 리스트 데이터 세팅하기 - 사용 시약이 없는 데이터가 존재할 수도 있기 때문에 여부 확인 필요
        if (result['useReagent']) {
            result['useReagent'].list = data?.reagents
                ? [...data['reagents']]
                : [];
        }
        // 표준물질/작업표준물질/내부표준물질은 마스터(project_test_report_form 의
        // std_material·task_std_material·inner_std_material)에 값이 있어도 가져오지 않는다.
        // 이관 전(release)과 같은 동작 — 시험자가 그날 쓴 물질을 직접 적는 표라
        // 마스터 값을 미리 채워 두면 확인 없이 그대로 저장될 수 있다.
        // 사용시약(reagents)만 종전대로 시딩한다. (2026-08-05 사용자 확정)

        //시험분석결과 데이터 세팅 (subCategory 가 있을 경우);
        const subCategoryData = [...state.analysisResultList];
        const uniqueSubCategory = getUniqueOrderedSubCategories(
            state.analysisResultList,
        );

        // customCategory 처리: isCustom이 설정된 formKey에만 적용 (BOD 대응으로 customCategoryMap 사용)
        if (data.customCategoryMap) {
    Object.keys(result).forEach(key => {
        if (!result[key].isCustom) return;
        const customCategory = data.customCategoryMap[key];
        if (!customCategory) return;

        // fixedRows 여부 확인 - BOD용
        let configs = CUSTOM_CATEGORY_CONFIG[state.testId];
        if (configs && !Array.isArray(configs)) configs = [configs];
        const config = configs?.find(c => c.formKey === key);

        if (config?.fixedRows) {
            result[key].orgLength =0;
            result[key].isSubCategory = false;
            result[key].subCategoryLength = customCategory.length;
            result[key].uniqueSubCategory = customCategory;
            result[key].list = [...customCategory];
            return;
        }

        const { list, orgLength } = flattenSubCategories(
            subCategoryData,
            () => customCategory,
            (sub, idx, index) => {
                if (idx !== 0) return '';
                const item = subCategoryData[index];
                return item?.sampleNo ?? item?.subCategory?.[0]?.sampleNo ?? '';
            },
        );
        result[key].orgLength = orgLength;
        result[key].isSubCategory = true;
        result[key].subCategoryLength = customCategory.length;
        result[key].uniqueSubCategory = customCategory;
        result[key].list = [...list];
    });
}

        // 일반 analysisResult 처리 (isCustom이 아닌 키만)
        const analysisResultKeys = getAnalysisResultKeys(result);
        analysisResultKeys.forEach(analysisKey => {
            if (subCategoryData[0]?.subCategory) {
                const { list, orgLength } = flattenBySubCategory(subCategoryData);
                result[analysisKey].orgLength = orgLength;
                result[analysisKey].isSubCategory = true;
                result[analysisKey].subCategoryLength = uniqueSubCategory.length;
                result[analysisKey].list = [...list];
            } else {
                result[analysisKey].list = subCategoryData;
            }
        });

        // v2(동적 일지) 블록 시딩: form_data[2].engine==='v2' 이면 각 블록 섹션에 시료 base 행 주입
        const v2meta = data.form_data[2];
        if (
            v2meta &&
            v2meta.engine === 'v2' &&
            state.v2Samples &&
            Array.isArray(state.v2Samples.samples)
        ) {
            // 시료분석결과·CCV 가 같은 이름을 쓰도록 규칙을 하나로 (수질만 lab_name)
            const parentLabel = testItemDisplayName(data, data.category);
            seedV2JournalBlocks({
                result,
                formData: data.form_data,
                v2Samples: { ...state.v2Samples, testId: state.testId },
                parentLabel,
            });
        }

        Object.keys(result).forEach(key => {
            const includeDefaultValueForms = result[key]?.headers?.filter(
                el => typeof el.default !== 'undefined' && el.default !== null,
            );
            includeDefaultValueForms.forEach(dv => {
                result[key].list.forEach(item => {
                    item[dv.key] = dv.default;
                });
            });
        });

        // journalTableForm 설정
        state.journalTableForm = { ...result };

        // journalForm 기본 데이터 설정
        state.journalForm = { ...data };

        applyFormDataOverrides(state, data.form_data, uniqueSubCategory);
        if (
            v2meta &&
            v2meta.engine === 'v2' &&
            state.v2Samples
        ) {
            applyV2PerChildSectionMeta(
                state.journalTableForm,
                state.v2Samples,
                state.journalForm,
                data.form_data,
            );
            applyV2ColumnGroupVisibility(
                state.journalTableForm,
                data.form_data,
                state.v2Samples,
            );
        }
        applyCcvSettings(state, data.form_data, state.v2Samples);

        const assignPayload = {
            cvvType: '',
            cvvType2: '',
            cvvType3: '',
        };
        // v2: applyV2PerChildSectionMeta 가 설정한 uniqueSubCategory 유지
        if (!(v2meta?.engine === 'v2' && state.journalForm.uniqueSubCategory?.length)) {
            assignPayload.uniqueSubCategory = uniqueSubCategory;
        }
        Object.assign(state.journalForm, assignPayload);

        applyTestMethodSettings(state, data);

        // 복수 기기(devices≥2) 템플릿만 journalForm.machine.devices 에 반영 — 1대 항목은 미개입
        if (state.journalTableForm['machine'] && state.journalForm.machine) {
            const m = state.journalTableForm['machine'];
            const formDevices = state.journalForm.machine.devices;
            if (
                !(Array.isArray(formDevices) && formDevices.length >= 2)
                && Array.isArray(m.devices)
                && m.devices.length >= 2
            ) {
                state.journalForm.machine.devices = m.devices.map((d) => ({ ...d }));
            }
        }
    },
    setAnalysisResult: (state, data) => {
        state.analysisResultList = [...data];
    },
    setV2Samples: (state, data) => {
        state.v2Samples = data;
    },
    applySpecificFormOverrides: (state, overrides) => {
        // 셋은 서로 다른 설정이라 각각 독립으로 본다.
        // 하나라도 «둘 다 있을 때만» 식으로 묶으면, 한쪽이 비어 있는 항목에서
        // 나머지 설정까지 통째로 무시된다 (loadJournal 주석 참조).
        if (overrides.digits != null) {
            state.journalForm.digits = overrides.digits;
        }
        if (overrides.digits_last != null && overrides.digits_last !== '') {
            state.journalForm.digits_last = overrides.digits_last;
        }
        if (overrides.concentration_limit != null) {
            state.journalForm.concentration_limit = overrides.concentration_limit;
        }
    },
    /** loadJournalForm(testId) 직후: 시험일지 핸들러 매칭용 id를 API 폼 기준 testId로 고정 */
    setJournalFormId: (state, testId) => {
        if (!testId || !state.journalForm) return;
        state.journalForm.id = testId;
    },
    setEnteredResult: (state, { data, isBeforeSelect = false, skipHeaderRemap = false }) => {
        if (!data) return;

        const formKey = [
            ...state.journalFormKeys[0],
            ...state.journalFormKeys[1],
        ];

        // form.sampleMethod 가 존재할 경우 (즉, 다른 시험방법으로 저장했음이 존재할 경우)
        // 서버에서 내려준 form_data 구조를 journalFormKeys 순서에 맞춰 headers 에 매핑
        // skipHeaderRemap: 시험방법을 명시적으로 변경한 경우, 이미 loadJournalFormData에서 설정된 헤더를 유지
        if (!skipHeaderRemap && data.sampleMethod && Array.isArray(data.form_data)) {
            const topKeys = state.journalFormKeys[0] || [];
            const bottomKeys = state.journalFormKeys[1] || [];

            const mapSectionHeaders = (sectionIndex, keys) => {
                const section = data.form_data[sectionIndex];
                if (!Array.isArray(section)) return;

                section.forEach((headerGroup, idx) => {
                    const key = keys[idx];
                    if (
                        !key ||
                        !state.journalTableForm[key] ||
                        !Array.isArray(headerGroup)
                    )
                        return;

                    assignHeaders(state.journalTableForm[key], headerGroup, {
                        forceSampleNoLabel: isDisposalTestId(
                            state.journalForm?.id || state.testId,
                        ),
                    });
                });
            };

            // 상단(form_data[0]) + 하단(form_data[1]) 공통 처리
            mapSectionHeaders(0, topKeys);
            mapSectionHeaders(1, bottomKeys);
        }

        // 저장본 form_data 에서 열 단위(unit)만 복원.
        // - 템플릿(loadJournalFormData)이 만든 현재 headers 구조는 그대로 유지(신규 구조 1순위).
        // - 저장본과 현재 헤더의 key 가 일치하고, 현재 헤더에 unit 속성이 정의된 항목에만 덮어씀.
        // - 이전 시험일지 불러오기(isBeforeSelect) 에도 단위는 함께 세팅(사용자 요구).
        // - 시료 시험방법 변경(skipHeaderRemap) 흐름에는 적용하지 않음.
        if (!skipHeaderRemap && Array.isArray(data.form_data)) {
            const applyUnitFromSaved = (sectionIndex, keys) => {
                const section = data.form_data[sectionIndex];
                if (!Array.isArray(section)) return;
                section.forEach((headerGroup, idx) => {
                    const key = keys[idx];
                    const tableForm = state.journalTableForm[key];
                    if (
                        !key ||
                        !tableForm ||
                        !Array.isArray(tableForm.headers) ||
                        !Array.isArray(headerGroup)
                    )
                        return;
                    const unitMap = new Map();
                    headerGroup.forEach(h => {
                        if (!h) return;
                        const hk = h.headerKey ?? h.key;
                        if (!hk) return;
                        if (typeof h.unit !== 'undefined') unitMap.set(hk, h.unit);
                    });
                    if (unitMap.size === 0) return;
                    tableForm.headers.forEach(h => {
                        if (!h) return;
                        const hk = h.headerKey ?? h.key;
                        if (!hk) return;
                        // 현재(템플릿) 헤더에 unit 이 정의된 열만 복원 — 신규 구조에서 unit 이 제거된 열에는 붙이지 않음
                        if (typeof h.unit === 'undefined') return;
                        if (unitMap.has(hk)) h.unit = unitMap.get(hk);
                    });
                });
            };
            applyUnitFromSaved(0, state.journalFormKeys[0] || []);
            applyUnitFromSaved(1, state.journalFormKeys[1] || []);
        }
        //info가 있을 경우 데이터 변경
        //subCategory 대응도 info 가 있을 경우를 대비해서 진행
        // skipHeaderRemap: 시료 시험방법 변경 시 loadJournalFormData의 사용기기 템플릿 유지
        // (저장된 일지는 getResult가 라우트 testId 기준 이전 machine을 주어 덮어쓰는 경우가 있음)
        if (data['machine'] && !skipHeaderRemap) {
            state.journalTableForm['machine'].list = Array.isArray(
                data['machine']?.data,
            )
                ? [...data['machine'].data]
                : [];
            // 저장된 devices 가 비어 있으면 마스터(템플릿) 복수 기기(발열량 ①② 등)를 유지
            if (
                Array.isArray(data['machine']?.devices) &&
                data['machine'].devices.length
            ) {
                state.journalTableForm['machine'].devices = data['machine'].devices.map((d) => ({
                    ...d,
                }));
            }
            // maker 만 표 폼에 반영. name(기기 모델명)은 journalForm.machine 전용
            // (표 폼 name → sectionTitle 로 조작조건 제목에 기기명이 뜨는 회귀 방지)
            const savedMaker = data['machine']?.maker;
            if (savedMaker != null && String(savedMaker).trim() !== '') {
                state.journalTableForm['machine'].maker = savedMaker;
            }
        }
        formKey.forEach(key => {
            if (data.optionalForm) {
                state.journalTableForm[key].optionalForm.forEach(
                    (optional, idx) => {
                        state.journalTableForm[key].optionalForm[idx].value =
                            data.optionalForm[key]?.[optional.key];
                        if (optional.key === 'inUseVolume') {
                            const u = data.optionalForm[key]?.inUseVolumeUnit;
                            // null/undefined(미저장)만 기본 mL — 빈 문자열('')은 유지
                            if (u != null) {
                                state.journalTableForm[key].optionalForm[
                                    idx
                                ].unit = String(u).trim();
                            } else {
                                state.journalTableForm[key].optionalForm[
                                    idx
                                ].unit = DEFAULT_IN_USE_VOLUME_UNIT;
                            }
                            const lu = data.optionalForm[key]?.inUseVolumeLabelUnit;
                            const stdHdr = (
                                state.journalTableForm[key].headers || []
                            ).find((h) => h.key === 'stdUseVolume');
                            const stdUnit =
                                stdHdr?.unit != null && String(stdHdr.unit).trim() !== ''
                                    ? String(stdHdr.unit).trim()
                                    : '';
                            if (lu != null && String(lu).trim() !== '') {
                                state.journalTableForm[key].optionalForm[
                                    idx
                                ].labelUnit = String(lu).trim();
                            } else {
                                // 괄호 단위 기본 = 표준 사용량 단위 (값 옆 unit 과 분리)
                                state.journalTableForm[key].optionalForm[
                                    idx
                                ].labelUnit = resolveInUseVolumeUnit(stdUnit);
                            }
                        }
                    },
                );
            }

            // 시험방법 변경 시(skipHeaderRemap=true) analysisResult 의 sampleNo 보존
            const prevSampleNos = (skipHeaderRemap && key.includes('analysisResult'))
                ? (state.journalTableForm[key]?.list || []).map(row => row.sampleNo)
                : null;

            if (data[key]) {
                //2024.12.04 grace - 시험일지 불러오기일 경우에는 시험결과만 데이터 안불러오도록 조건 처리
                if (isBeforeSelect) {
                    if (!key.includes('analysisResult')) {
                        state.journalTableForm[key].list = [...data[key]];
                    }
                } else {
                    state.journalTableForm[key].list = [...data[key]];
                }
            }
            if (!isBeforeSelect && data[key]?.[0]?.subCategory) {
                const { list, orgLength } = flattenBySubCategory(data[key]);
                state.journalTableForm[key].orgLength = orgLength;
                state.journalTableForm[key].isSubCategory = true;
                state.journalTableForm[key].list = [...list];
            }

            // 보존해 둔 sampleNo 를 새 리스트에 복원
            if (prevSampleNos) {
                const newList = state.journalTableForm[key]?.list;
                if (newList) {
                    prevSampleNos.forEach((sno, idx) => {
                        if (idx < newList.length && sno !== undefined) {
                            newList[idx] = { ...newList[idx], sampleNo: sno };
                        }
                    });
                }
            }
        });

        // v2 perChild: 저장본 로드 후 시료별 그룹·시료번호 병합 UI 복원
        // form_data 의 isSubCategory 를 먼저 반영한 뒤, perChild 메타로 덮어쓴다
        // (applyFormDataOverrides 가 나중에 오면 isSubCategory:false 가 perChild UI 를 깨뜨림)
        applyFormDataOverrides(state, state.journalForm.form_data, state.journalForm.uniqueSubCategory);
        if (
            !isBeforeSelect &&
            state.v2Samples?.engine === 'v2'
        ) {
            // 구버전 저장(시험항목=시료분석결과 1행) → 자식별 다행으로 재시딩 + 입력값 머지
            if (Array.isArray(state.v2Samples.samples)) {
                const parentLabel = testItemDisplayName(
                    state.journalForm,
                    state.journalForm?.category,
                );
                seedV2JournalBlocks({
                    result: state.journalTableForm,
                    formData: state.journalForm.form_data || [],
                    v2Samples: { ...state.v2Samples, testId: state.testId },
                    parentLabel,
                    mergeExisting: true,
                });
            }
            applyV2PerChildSectionMeta(
                state.journalTableForm,
                state.v2Samples,
                state.journalForm,
                state.journalForm.form_data,
            );
            applyV2ColumnGroupVisibility(
                state.journalTableForm,
                state.journalForm.form_data,
                state.v2Samples,
            );
        }

        const ccvItem = applyCcvSettings(state, state.journalForm.form_data, state.v2Samples);
        if (ccvItem) {
            data.exceptDeviation = ccvItem.isDeviation ?? null;
            if (state.v2Samples?.engine !== 'v2') {
                data.isCcvSubCategory = ccvItem.isSubCategory;
            }
        }
        const preservedCcvSubCategory = state.journalForm.isCcvSubCategory;
        const preservedUniqueSubCategory = state.journalForm.uniqueSubCategory;
        // 저장본 machine.name/maker 가 빈 문자열일 때 마스터 값을 잃지 않도록 병합 전 보존
        const masterMachineName = state.journalForm?.machine?.name;
        const masterMachineMaker = state.journalForm?.machine?.maker;

        if (skipHeaderRemap) {
            // 시험방법 변경 시: loadJournalFormData에서 설정된 form_data/sampleMethod/method·machine 유지
            // 저장된 getResult.machine은 이전 시험방법 값이므로 병합하지 않음
            // 저장된 result.id(이전에 선택했던 시험방법 testId)는 병합하면 안 됨 → journalTableForm 핸들러가
            // journalForm.id로 WATER_TEST_017 등을 판별하므로, id 덮어쓰기 시 BOD·기타 testIds 매칭이 깨짐
            const {
                form_data,
                sampleMethod,
                method,
                machine: _enteredMachine,
                id: _savedResultTestId,
                ...restData
            } = data;
            state.journalForm = { ...state.journalForm, ...restData };
        } else {
            // v2: 표시/레이아웃 메타는 마스터 form_data 유지.
            // 구형 저장본 form_data 가 layout.blocks 없이 덮어쓰면
            // 삼성분·원소분석 요약(analysisResult)이 UI에 잘못 노출된다.
            // 단위 복원은 위에서 저장본 → headers 로 이미 반영됨.
            const masterFormData = state.journalForm?.form_data;
            const keepMasterFormData =
                Array.isArray(masterFormData) &&
                masterFormData[2]?.engine === 'v2';
            state.journalForm = { ...state.journalForm, ...data };
            if (keepMasterFormData) {
                state.journalForm.form_data = masterFormData;
            }
            applyTestMethodSettings(state, data);
        }
        if (state.v2Samples?.engine === 'v2') {
            if (preservedCcvSubCategory) {
                state.journalForm.isCcvSubCategory = preservedCcvSubCategory;
            }
            if (preservedUniqueSubCategory?.length) {
                state.journalForm.uniqueSubCategory = preservedUniqueSubCategory;
            }
        }
        // journal2 등에서 form.machine.data 사용, store는 journalTableForm.machine.list가 원본 — 항상 일치
        // devices 는 ≥2 일 때만 유지(발열량). 1대 항목에 devices:[] 를 붙이지 않음
        if (state.journalTableForm['machine']) {
            const m = state.journalTableForm['machine'];
            const enteredDevices = state.journalForm.machine?.devices;
            const templateDevices = m.devices;
            const resolvedDevices =
                Array.isArray(enteredDevices) && enteredDevices.length >= 2
                    ? enteredDevices.map((d) => ({ ...d }))
                    : Array.isArray(templateDevices) && templateDevices.length >= 2
                        ? templateDevices.map((d) => ({ ...d }))
                        : null;
            const pickNonEmpty = (...vals) => {
                for (const v of vals) {
                    if (v != null && String(v).trim() !== '') return v;
                }
                return '';
            };
            // name 은 journalForm 전용(표 폼 name ≠ 섹션 제목). 빈 저장본은 마스터로 폴백.
            const nextMachine = {
                ...(state.journalForm.machine || {}),
                name: pickNonEmpty(
                    state.journalForm.machine?.name,
                    masterMachineName,
                ),
                maker: pickNonEmpty(
                    m.maker,
                    state.journalForm.machine?.maker,
                    masterMachineMaker,
                ),
                data: Array.isArray(m.list) ? [...m.list] : [],
            };
            if (resolvedDevices) {
                nextMachine.devices = resolvedDevices;
            } else {
                delete nextMachine.devices;
            }
            state.journalForm.machine = nextMachine;
        }
    },
    // form_data 로부터 headers (formula 포함) 만 갱신
    // - list/optionalForm.value/사용기기 등 사용자 입력값은 그대로 유지
    // - DB 에서 formula 만 변경한 경우, 수동 동기화 시 새 formula 가 반영되도록 사용
    _refreshHeadersFromFormData: (state, data) => {
        if (!data || !Array.isArray(data.form_data)) return;

        const allForms = [
            ...(data.form_data[0] || []),
            ...(data.form_data[1] || []),
        ];

        allForms.forEach(form => {
            const target = state.journalTableForm[form.key];
            if (!target || !Array.isArray(form.form)) return;
            assignHeaders(target, form.form, {
                forceSampleNoLabel: isDisposalTestId(state.journalForm?.id || state.testId),
            });
        });

        // PDF 출력 등에서 참조하는 journalForm.form_data 도 최신화
        if (state.journalForm) {
            state.journalForm.form_data = data.form_data;
        }

        applyFormDataOverrides(
            state,
            data.form_data,
            state.journalForm?.uniqueSubCategory,
        );
        applyCcvSettings(state, data.form_data, state.v2Samples);
    },
    setImportSampleData: (state, payload) => {
        const { category, sample, testId } = payload;

        // v2(동적 일지): perChildBlocks 다행 재시딩 + 기존 입력값 머지
        if (state.v2Samples?.engine === 'v2' && Array.isArray(state.v2Samples.samples)) {
            const parentLabel = testItemDisplayName(
                state.journalForm,
                state.journalForm?.category,
            );
            const specimenByNo = new Map(
                (sample || []).map(s => [s.no || s.sampleNo, s]),
            );

            const refreshedSamples = state.v2Samples.samples.map(s => {
                const spec = specimenByNo.get(s.sampleNo);
                if (!spec) return s;
                return {
                    ...s,
                    sampleId: spec.id || s.sampleId,
                    sampleName: spec.name || s.sampleName,
                };
            });
            const refreshedV2Samples = {
                ...state.v2Samples,
                samples: refreshedSamples,
                testId,
            };
            // loadAnalysisResult가 전용 API에서 읽은 childIsTest에는 미선택(false) 자식도 포함된다.
            // 일반 시료 목록은 미선택 자식을 생략하므로 여기서 재구성하면 부모 선택 폴백으로
            // 미선택 자식이 true가 될 수 있다. 최신 전용 API 선택 맵을 그대로 유지한다.
            state.v2Samples = refreshedV2Samples;

            seedV2JournalBlocks({
                result: state.journalTableForm,
                formData: state.journalForm?.form_data || [],
                v2Samples: refreshedV2Samples,
                parentLabel,
                mergeExisting: true,
            });
            applyV2PerChildSectionMeta(
                state.journalTableForm,
                refreshedV2Samples,
                state.journalForm,
                state.journalForm?.form_data || [],
            );
            applyV2ColumnGroupVisibility(
                state.journalTableForm,
                state.journalForm?.form_data || [],
                refreshedV2Samples,
            );
            applyCcvSettings(
                state,
                state.journalForm?.form_data || [],
                refreshedV2Samples,
            );
            return;
        }

        //subCategory가 있는지 확인 용도 배열 체크
        const matchParentCategory = category.filter(
            c => c.parent_id === testId && !c.is_report_hide,
        );
        // 빠른 조회용 Map
        const categoryMap = new Map(
            matchParentCategory.map(c => [c.testId, c]),
        );
        const importSampleList = {};
        const totalKeys = new Set();
        if (matchParentCategory.length === 0) {
            const activeSamples = sample.filter(
                s => s.testResult?.[testId]?.isTest === true,
            );

            const existingMap = new Map();
            state.analysisResultList.forEach(item => {
                if (item.sampleNo) existingMap.set(item.sampleNo, item);
            });

            const analysisResultKey = getAnalysisResultKeys(state.journalTableForm)[0];
            const formHeaders = state.journalTableForm[analysisResultKey]?.headers || [];
            const templateBase = state.analysisResultList[0] || null;

            const createNewEntry = (sItem) => {
                const entry = {};
                formHeaders.forEach(h => {
                    if (h.headerKey) entry[h.headerKey] = '';
                });
                if (templateBase) {
                    if (templateBase.digits != null) entry.digits = templateBase.digits;
                    if (templateBase.digits_last != null) entry.digits_last = templateBase.digits_last;
                    if (templateBase.concentration_limit != null) entry.concentration_limit = templateBase.concentration_limit;
                    if (templateBase.concentration_equal != null) entry.concentration_equal = templateBase.concentration_equal;
                    if (templateBase.criteria_area_1 != null) entry.criteria_area_1 = templateBase.criteria_area_1;
                    if (templateBase.testCategory != null) entry.testCategory = templateBase.testCategory;
                }
                entry.sampleNo = sItem.sampleNo;
                entry.sampleName = sItem.name || sItem.sampleName || '';
                entry.sampleId = sItem.id || sItem.sampleId || '';
                return entry;
            };

            const newAnalysisList = activeSamples.map(sItem => {
                return existingMap.get(sItem.sampleNo) || createNewEntry(sItem);
            });

            const defaultHeaders = formHeaders.filter(
                h => h.default !== undefined && h.default !== null,
            );
            newAnalysisList.forEach(item => {
                if (!existingMap.has(item.sampleNo)) {
                    defaultHeaders.forEach(dv => {
                        item[dv.headerKey] = dv.default;
                    });
                }
            });

            const analysisResultKeys = getAnalysisResultKeys(state.journalTableForm);
            analysisResultKeys.forEach(analysisKey => {
                state.journalTableForm[analysisKey].list = newAnalysisList;
            });
            const activeSamplesForCustom = activeSamples
                .map(s => ({ sampleNo: s.sampleNo, testCategory: '' }));
            applyCustomCategoryImport(state, testId, activeSamplesForCustom);
            return;
        }
        sample.forEach(item => {
            const testResult = item.testResult || {};
            const matched = [];
            Object.keys(testResult).forEach(key => {
                const entry = testResult[key];
                if (entry && entry.isTest === true && categoryMap.has(key)) {
                    matched.push(categoryMap.get(key));
                    totalKeys.add(key);
                }
            });
            if (matched.length > 0) {
                importSampleList[item.sampleNo] = matched;
            }
        });

        // 전체 키 배열 (중복 제거)
        const importCategories = category.filter(c => totalKeys.has(c.testId));
        const orderMap = new Map(
            importCategories.map((c, idx) => [c.testId, idx]),
        );
        // 페놀류 027/102는 DB 카테고리 목록에서 doc_order 등으로 인덱스가 달라질 수 있음.
        // 시료별로 추출↔직접 전환 시에도 시험항목(열) 순서가 유지되도록 동일 정렬 키로 맞춤.
        const phenolOrderIndices = PHENOL_IDS.map(id => orderMap.get(id)).filter(
            v => v !== undefined,
        );
        if (phenolOrderIndices.length > 0) {
            const minPhenolIdx = Math.min(...phenolOrderIndices);
            PHENOL_IDS.forEach(id => {
                if (orderMap.has(id)) orderMap.set(id, minPhenolIdx);
            });
        }
        Object.keys(importSampleList).forEach(sNo => {
            importSampleList[sNo].sort((a, b) => {
                const ai = orderMap.get(a.testId) ?? Number.POSITIVE_INFINITY;
                const bi = orderMap.get(b.testId) ?? Number.POSITIVE_INFINITY;
                return ai - bi;
            });
        });

        // 새로운 analysisList 초기화
        const newAnalysisList = [];
        Object.keys(importSampleList).forEach((sampleNo, index) => {
            const sampleList = importSampleList[sampleNo];
            // 기존 그룹 정보 복제 (testCategory 등)
            const baseGroup = state.analysisResultList[index] || {
                testCategory: '',
            };

            newAnalysisList[index] = {
                subCategory: [],
                testCategory: baseGroup.testCategory,
            };

            // importSampleList 에 맞춰 subCategory 채우기
            sampleList.forEach(cat => {
                const label = importCategoryLabel(
                    cat,
                    state.journalForm?.category,
                );
                //   (backgroundConcentration: "0.00000" 등) 이 빈값으로 강제 초기화되는 회귀가 있어
                //   양쪽을 trim 한 비교로 보강한다.
                const existItem = (
                    state.analysisResultList[index]?.subCategory || []
                ).find(
                    sc =>
                        trimEqNonNull(sc.testCategory, label) ||
                        (cat.name != null &&
                            trimEqNonNull(sc.testCategory, cat.name)) ||
                        (cat.lab_name != null &&
                            trimEqNonNull(sc.testCategory, cat.lab_name)),
                );

                if (existItem) {
                    newAnalysisList[index].subCategory.push({
                        ...existItem,
                        testCategory: label,
                        sampleNo: sampleNo,
                    });
                } else {
                    // 없으면 기본 템플릿으로 새로 생성
                    const template =
                        state.analysisResultList[index]?.subCategory?.[0] || {};
                    const newItem = {
                        ...JSON.parse(JSON.stringify(template)),
                        testCategory: label,
                        criteria_area_1: cat.criteria_area_1,
                        lastConcentration: '',
                        markedConcentration: '',
                        reagentConcentration: '',
                        backgroundConcentration: '',
                        concentration_equal: false,
                        sampleNo: sampleNo,
                    };
                    newAnalysisList[index].subCategory.push(newItem);
                }
            });
        });

        state.newAnalysisList = newAnalysisList;
        const { list, orgLength } = flattenBySubCategory(newAnalysisList);

        const analysisResultKeys = getAnalysisResultKeys(state.journalTableForm);
        analysisResultKeys.forEach(analysisKey => {
            state.journalTableForm[analysisKey].orgLength = orgLength;
            state.journalTableForm[analysisKey].isSubCategory = true;
            state.journalTableForm[analysisKey].list = [...list];
        });
        const activeSamples2 = sample
            .filter(s => s.testResult?.[testId]?.isTest === true)
            .map(s => ({ sampleNo: s.sampleNo, testCategory: '' }));
        applyCustomCategoryImport(state, testId, activeSamples2);
    },
};

const actions = {
    //setting data
    loadJournal: async (
        { state, commit, dispatch },
        { projectId, testId, isBeforeSelect },
    ) => {
        //2024.12.04 grace - 시험일지 불러오기일 경우에는 시험결과만 데이터 안불러오도록 조건 처리
        commit('setProjectIds', { projectId, testId });
        if (!isBeforeSelect) {
            await dispatch('loadAnalysisResult', { projectId, testId });
        }
        await dispatch('loadBeforeJournal', testId);
        await dispatch('loadMachine');
        await dispatch('loadJournalFormData', testId);

        /**
         * 자리수·정량한계를 폼에 반영 (2026-08-26 현업 제보).
         *
         * 종전에는 `digits != null && concentration_limit != null` 로 **둘 다 있을 때만**
         * 적용했다. 그래서 정량한계가 없는 항목은 자리수를 마스터에서 아무리 바꿔도
         * 반영되지 않고 journalForm 기본값(1)이 그대로 남았다.
         * (전기전도도 WATER_TEST_094: digits=0 인데 정량한계가 null 이라 늘 1 로 표시)
         * 수질 152개 항목 중 59개가 «자리수는 있고 정량한계는 없는» 상태다.
         *
         * 서로 다른 설정이므로 각각 독립으로 적용한다.
         * digits_last(최종농도 자리수)도 함께 넘긴다 — 종전에는 전달 경로가 없었다.
         */
        const firstSample = state.analysisResultList[0];
        const metaSource =
            firstSample &&
            (firstSample.digits != null ||
                firstSample.digits_last != null ||
                firstSample.concentration_limit != null)
                ? firstSample
                : state.v2Samples?.engine === 'v2'
                  ? // v2 는 analysisResultList 가 비어 있으므로 첫 자식 메타를 쓴다
                    (state.v2Samples.children || []).find(
                        c =>
                            c.digits != null ||
                            c.digits_last != null ||
                            c.concentration_limit != null,
                    )
                  : null;
        if (metaSource) {
            commit('applySpecificFormOverrides', {
                digits: metaSource.digits,
                digits_last: metaSource.digits_last,
                concentration_limit: metaSource.concentration_limit,
            });
        }

        await dispatch('loadEnteredTestResult', {
            projectId,
            testId,
            isBeforeSelect,
        });
    },
    //시험일지 - 기기리스트 가져오기
    loadMachine: async ({ commit }) => {
        const res = await api.project.getMachine();
        commit('setMachine', res);
    },
    /**
     * 시험일지 - 이전 시험일지 목록 가져오기
     *
     * @param {string|{testId: string, keyword?: string, limit?: number}} payload
     *   문자열이면 testId 만 전달한 기존 호출 형태.
     *   검색은 서버에서 수행한다 — 최근 N건만 받아 화면에서 거르면 오래된 일지가 검색되지 않는다.
     */
    loadBeforeJournal: async ({ commit }, payload) => {
        const { testId, keyword, limit } =
            typeof payload === 'string' ? { testId: payload } : payload || {};
        if (!testId) return;
        const res = await api.project.getTestJournal(testId, {
            keyword,
            limit: limit ?? BEFORE_JOURNAL_LIMIT,
        });
        commit('setBeforeJournal', Array.isArray(res) ? res : []);
    },
    //시험일지 - 입력된 시험결과 가져오기
    loadEnteredTestResult: async (
        { state, commit, dispatch },
        { projectId, testId, isBeforeSelect, skipFormReload, skipHeaderRemap },
    ) => {
        const res = await api.project.getResult(projectId, testId);
        // 미작성 일지: GET 이 null / false 를 반환할 수 있음 (res.result 직접 접근 금지)
        if (!res) {
            commit('setEnteredResult', {
                data: null,
                isBeforeSelect: isBeforeSelect,
                skipHeaderRemap: skipHeaderRemap,
            });
            return;
        }
        let resultBody = res.result;
        if (typeof resultBody === 'string') {
            try {
                resultBody = JSON.parse(resultBody);
            } catch (e) {
                console.error('[loadEnteredTestResult] result JSON parse 실패', e);
                resultBody = null;
            }
        }
        if (!skipFormReload && resultBody?.sampleMethod && resultBody.sampleMethod !== testId) {
            await dispatch('loadJournalFormData', resultBody.sampleMethod);
        }
        commit('setEnteredResult', {
            data: resultBody,
            isBeforeSelect: isBeforeSelect,
            skipHeaderRemap: skipHeaderRemap,
        });
    },
    //시험일지 - form data 가져오기
    loadJournalFormData: async ({ state, commit }, testId) => {
    const res = await api.project.getJournalForm(testId);
    // 방어: form_data 미등록 항목(form_data NULL 등) — 빈 폼 구조로 정규화해 크래시 방지
    if (!Array.isArray(res.form_data)) res.form_data = [[], []];
    if (!Array.isArray(res.form_data[0])) res.form_data[0] = [];
    if (!Array.isArray(res.form_data[1])) res.form_data[1] = [];
    let configs = CUSTOM_CATEGORY_CONFIG[testId];
    if (configs) {
        if (!Array.isArray(configs)) configs = [configs];
        res.customCategoryMap = {};
        configs.forEach(config => {
            const allForms = [...(res.form_data[0] || []), ...res.form_data[1]];
            const targetForm = allForms.find(el => el.key === config.formKey);
            if (targetForm) {
                targetForm.isCustom = true;
            }
            res.customCategoryMap[config.formKey] = config.testCategories.map(testCategory => ({
                sampleNo: '',
                testCategory,
                ...config.template,
            }));
        });
    }
    commit('_setJournalFormData', res);
    commit('setJournalFormId', testId);
    return res;
},
    //시험일지 - 시험내 시험방법 변경 시 일부 form data 만 리로드
    //시료 목록(analysisResultList)은 시험방법이 바뀌어도 동일하므로 재로드하지 않음
    loadJournalFormDataBySampleMethod: async ({ state, commit, dispatch }, testId) => {
        await dispatch('loadMachine');
        await dispatch('loadJournalFormData', testId);
        await dispatch('loadEnteredTestResult', {
            projectId: state.projectId,
            testId,
            isBeforeSelect: false,
            skipFormReload: true,
            skipHeaderRemap: true,
        });
    },
    //시험일지 - 입력해야하는 시료 목록 가져오기
    loadAnalysisResult: async ({ state, commit }, { projectId, testId }) => {
        const res = await api.project.getSampleResultList(projectId, testId);
        // v2(동적 일지) 응답: 배열이 아닌 { engine:'v2', blocks, samples } 객체
        if (res && !Array.isArray(res) && res.engine === 'v2') {
            commit('setV2Samples', res);
            commit('setAnalysisResult', []);
        } else {
            commit('setV2Samples', null);
            commit('setAnalysisResult', res);
        }
    },
    //시험일지 - 시험결과 저장하기
    editJournal: async ({ state, commit }, payload) => {
        warnPerChildAnalysisBeforeSave(
            state.journalTableForm,
            state.journalForm,
            state.v2Samples,
        );
        const reformData = _setReformData(
            JSON.parse(
                JSON.stringify({
                    journalForm: state.journalForm,
                    journalTableForm: state.journalTableForm,
                    journalFormKeys: state.journalFormKeys,
                    paramsForm: payload.form,
                }),
            ),
        );
        const res = await api.project.updateJournalResult(
            payload.projectId,
            state.testId,
            reformData,
            payload.autoSave,
        );
        return res;
    },
    // 시험일지 - form_data (formula 포함) 만 DB 에서 다시 받아 headers 갱신
    // - list/입력값/사용기기 등은 그대로 유지 → 수동 동기화 등에서 안전하게 호출 가능
    // - 시험방법(sampleMethod) 변경 후에도 현재 적용 중인 testId 의 최신 formula 반영용
    reloadJournalFormHeaders: async ({ commit }, testId) => {
        if (!testId) return;
        const res = await api.project.getJournalForm(testId);
        if (!res || !res.form_data) return;
        commit('_refreshHeadersFromFormData', res);
    },
    //시험일지 - 분석결과 수동 동기화
    //isTest 값이 바뀌었을 때를 대비해서 다시 한 번 업데이트 진행
    //기존 값은 유지한 채, 항목만 덮어씌우기 추가
    importSampleData: async ({ commit, dispatch }, payload) => {
        const { projectId, testId, list } = payload;
        await dispatch('loadAnalysisResult', { projectId, testId });
        let result = await api.project.getCategory(projectId, '', 'lab');
        if (list && list.length > 0) {
            result = list;
        }
        const sample = await api.project.getSampleList(projectId, false);
        // const res = await api.project.getSampleResultList(projectId, testId);
        await commit('setImportSampleData', {
            category: result.list,
            sample: sample,
            testId: testId,
        });
    },
};

function _setReformData(snapshot) {
    const reform = { ...snapshot.journalTableForm };
    const reformDataKeys = Object.keys(reform);
    const optionalForm = {};
    if (snapshot.paramsForm) {
        snapshot.journalForm = { ...snapshot.journalForm, ...snapshot.paramsForm };
    }
    // 물리적조성 등 form_data 섹션이 비어 있어도 저장이 깨지지 않도록 가드
    if (!Array.isArray(snapshot.journalForm.form_data)) {
        snapshot.journalForm.form_data = [[], []];
    }
    if (!Array.isArray(snapshot.journalForm.form_data[0])) {
        snapshot.journalForm.form_data[0] = [];
    }
    if (!Array.isArray(snapshot.journalForm.form_data[1])) {
        snapshot.journalForm.form_data[1] = [];
    }
    reformDataKeys.forEach(key => {
        snapshot.journalFormKeys[0].forEach((initKey, idx) => {
            if (initKey === key) {
                snapshot.journalForm.form_data[0][idx] = [...reform[key].headers];
            }
        });
        snapshot.journalFormKeys[1].forEach((initKey, idx) => {
            if (initKey === key) {
                snapshot.journalForm.form_data[1][idx] = [...reform[key].headers];
            }
        });
        if (reform[key].optionalForm) {
            reform[key].optionalForm.forEach((optional, idx) => {
                if (!optionalForm[key]) {
                    optionalForm[key] = {};
                }
                optionalForm[key][optional.key] =
                    reform[key].optionalForm[idx].value;
                if (optional.key === 'inUseVolume') {
                    const optRow = reform[key].optionalForm[idx];
                    // 값 옆 단위: 빈 문자열 허용 (mL 강제 금지)
                    optionalForm[key].inUseVolumeUnit =
                        optRow.unit != null ? String(optRow.unit).trim() : '';
                    const stdHdr = (reform[key].headers || []).find(
                        (h) => h.key === 'stdUseVolume',
                    );
                    const stdUnit =
                        stdHdr?.unit != null && String(stdHdr.unit).trim() !== ''
                            ? String(stdHdr.unit).trim()
                            : '';
                    optionalForm[key].inUseVolumeLabelUnit = resolveInUseVolumeUnit(
                        optRow.labelUnit,
                        stdUnit,
                    );
                }
            });
            snapshot.journalForm.optionalForm = { ...optionalForm };
        }

        const orgLength = reform[key].orgLength;
        if (orgLength && reform[key].isSubCategory) {
            const tempResult = {};
            reform[key].list.forEach(el => {
                const start = el.categoryIndex?.start;
                if (start == null) return;
                if (typeof tempResult[start] === 'undefined') {
                    tempResult[start] = {
                        testCategory: snapshot.journalForm.name,
                        subCategory: [],
                    };
                }
                tempResult[start].subCategory.push(el);
            });
            reform[key] = Object.keys(tempResult).map(k => tempResult[k]);
        } else {
            if (reform[key].list) {
                reform[key] = reform[key].list;
            }
        }
    });

    // paramsForm.machine 이 journalForm에 merge된 상태.
    // devices≥2 만 저장(발열량 등). 1대 항목은 name/maker/data 만 유지
    const savedMachine = snapshot.journalForm.machine || {};
    reform['machine'] = {
        name: savedMachine.name,
        data: reform['machine'],
        maker: savedMachine.maker,
        ...(Array.isArray(savedMachine.devices) && savedMachine.devices.length >= 2
            ? { devices: savedMachine.devices.map((d) => ({ ...d })) }
            : {}),
    };
    // physComposition 은 journalTableForm 밖(paramsForm)에만 있으므로 명시 보존
    const merged = { ...snapshot.journalForm, ...reform };
    if (snapshot.paramsForm?.physComposition != null) {
        merged.physComposition = snapshot.paramsForm.physComposition;
    } else if (snapshot.journalForm?.physComposition != null) {
        merged.physComposition = snapshot.journalForm.physComposition;
    }
    return merged;
}

//subCategory 중복값 제거 + 순서 유지하여 전달
function getUniqueOrderedSubCategories(data) {
    const seen = new Set();
    const result = [];
    data.forEach(group => {
        if (group?.subCategory?.length > 0) {
            group.subCategory.forEach(item => {
                const key = item.testCategory;
                if (!seen.has(key)) {
                    seen.add(key);
                    result.push(item); // 전체 객체를 유지하고 싶으면 이 방식
                }
            });
        }
    });

    return result;
}

export default { namespaced: true, state, getters, mutations, actions };
